/**
 * Parent email verification placement — GrowthBook WHEN only (sc-222848).
 *
 * Flag `sc-parent-email-verification-placement`:
 * - `parent_onboarding`          → Variant A (gate after employer eligibility, before care-type)
 * - `dashboard`                  → Variant B (restrict dashboard after onboarding)
 * - `no_verification_required`   → Variant C (control — never force EV for this experiment)
 *
 * Kept separate from the sitter placementPolicy so the two experiments cannot collide.
 *
 * Safe fallback: unknown values → `dashboard`.
 */
import { getFeatureValue, syncGrowthbookIdentity } from '@/lib/growthbook';

/** Variant A — verify after employer eligibility, before care-needs onboarding. */
export const PARENT_PLACEMENT_ONBOARDING = 'parent_onboarding';

/** Variant B (also the safe default for unknowns) — verify on the parent dashboard. */
export const PARENT_PLACEMENT_DASHBOARD = 'dashboard';

/** Variant C — do not force email verification (experiment control). */
export const PARENT_PLACEMENT_NO_VERIFICATION_REQUIRED = 'no_verification_required';

/** GrowthBook feature key — keep in sync with the Sittercity GB project. */
export const PARENT_EMAIL_VERIFICATION_PLACEMENT_FEATURE_KEY = 'sc-parent-email-verification-placement';

export type KnownParentEmailVerificationPlacement =
  | typeof PARENT_PLACEMENT_ONBOARDING
  | typeof PARENT_PLACEMENT_DASHBOARD
  | typeof PARENT_PLACEMENT_NO_VERIFICATION_REQUIRED;

export type ParentEmailVerificationPlacement = KnownParentEmailVerificationPlacement | string;

export type ParentEmailVerificationMoment = 'onboarding' | 'dashboard';

export type ParentEmailVerificationPolicyInput = {
  verified: boolean;
  isParent: boolean;
  isCorporate?: boolean;
  placement: ParentEmailVerificationPlacement;
  moment: ParentEmailVerificationMoment;
};

export function normalizeParentEmailVerificationPlacement(
  placement: ParentEmailVerificationPlacement
): KnownParentEmailVerificationPlacement {
  if (placement === PARENT_PLACEMENT_ONBOARDING) {
    return PARENT_PLACEMENT_ONBOARDING;
  }

  if (placement === PARENT_PLACEMENT_NO_VERIFICATION_REQUIRED) {
    return PARENT_PLACEMENT_NO_VERIFICATION_REQUIRED;
  }

  return PARENT_PLACEMENT_DASHBOARD;
}

export function getParentEmailVerificationPlacement(): KnownParentEmailVerificationPlacement {
  syncGrowthbookIdentity();
  const raw = getFeatureValue(PARENT_EMAIL_VERIFICATION_PLACEMENT_FEATURE_KEY, PARENT_PLACEMENT_DASHBOARD);
  return normalizeParentEmailVerificationPlacement(raw);
}

/**
 * Pure placement policy for the parent experiment:
 * - verified / non-parent / corporate → never
 * - Variant A: onboarding moment (dashboard moment is a fallback)
 * - Variant B: dashboard moment only
 * - Variant C: never
 */
export function shouldRequireParentEmailVerificationNow(input: ParentEmailVerificationPolicyInput): boolean {
  if (input.verified || !input.isParent || input.isCorporate) {
    return false;
  }

  const placement = normalizeParentEmailVerificationPlacement(input.placement);
  if (placement === PARENT_PLACEMENT_NO_VERIFICATION_REQUIRED) {
    return false;
  }

  if (input.moment === 'onboarding') {
    return placement === PARENT_PLACEMENT_ONBOARDING;
  }

  // Dashboard moment: Variant A (fallback) and Variant B both require verification.
  return true;
}
